import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import PanelTemplate from "../../templates/Panel";
import PageHeader from "../../components/PageHeader";
import Box from "../../components/Box";
import Button from "../../components/Button";
import Input from "../../components/Input";
import InputDate from "../../components/InputDate";
import InputSelect from "../../components/InputSelect";
import Icon from "../../components/Icon";

import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { flattenCategories, getCategoryLabel } from "../Finance/helpers";

import ReportResult from "./components/ReportResult";
import {
    CHART_TYPES,
    ORDER_DIRECTIONS,
    REFERENCE_ENDPOINTS,
    defaultConfig,
    emptyFilterValue,
    filterOperatorFor,
    normalizeFilter,
} from "./helpers";

const PREVIEW_DELAY = 400;

/**
 * Construtor de relatório (BDM-1040).
 *
 * O usuário compõe DENTRO de uma fonte de dados: escolhe colunas, filtros,
 * período e ordenação a partir do que a fonte declara. Não existe escolher
 * tabela nem join — é essa restrição que mantém o módulo previsível e o banco
 * de pé.
 *
 * Agrupar é UM select, com uma dimensão só: "Não agrupar" lista título a
 * título; qualquer outra escolha soma os valores por aquela dimensão. Um
 * botão "agrupar" por campo já foi tentado e confundia — e o gráfico só sabe
 * desenhar uma dimensão de qualquer jeito.
 */
export const ReportEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = !id;

    const [datasets, setDatasets] = useState([]);
    const [datasetKey, setDatasetKey] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [config, setConfig] = useState(defaultConfig(null));

    const [result, setResult] = useState(null);
    const [running, setRunning] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [references, setReferences] = useState({});

    const dataset = useMemo(
        () => datasets.find(item => item.key === datasetKey) ?? null,
        [datasets, datasetKey]
    );

    const grouped = config.group_by.length > 0;
    const hasPeriod = Boolean(config.start_date && config.end_date);

    // Agrupado, a única dimensão da tabela é a do agrupamento — outra não
    // teria por onde entrar. Detalhado, "Títulos" (contagem) não faz sentido
    // linha a linha e some da lista. As colunas escondidas ficam guardadas no
    // config e voltam se o usuário desagrupar; só não vão no payload.
    const allDimensions = (dataset?.fields ?? []).filter(field => field.role === 'dimension');
    const dimensions = allDimensions.filter(field => !grouped || config.group_by.includes(field.key));
    const measures = (dataset?.fields ?? []).filter(field =>
        field.role === 'measure' && (grouped || !field.grouped_only)
    );
    const availableKeys = [...dimensions, ...measures].map(field => field.key);
    const chosenColumns = config.columns.filter(key => availableKeys.includes(key));

    useEffect(() => {
        api.get(`/report/dataset`)
            .then(res => {
                setDatasets(res.data.resources);

                if (isNew) {
                    const first = res.data.resources[0] ?? null;
                    setDatasetKey(first?.key ?? null);
                    setConfig(defaultConfig(first));
                    setLoading(false);
                    return;
                }

                return api.get(`/report/${id}`).then(report => {
                    const resource = report.data.resource;
                    const source = res.data.resources.find(item => item.key === resource.dataset) ?? null;
                    const saved = { ...defaultConfig(source), ...resource.config };

                    setName(resource.name);
                    setDescription(resource.description ?? '');
                    setDatasetKey(resource.dataset);
                    setConfig({
                        ...saved,
                        filters: (saved.filters ?? []).map(filter => normalizeFilter(filter, source?.fields?.find(item => item.key === filter.field))),
                    });
                    setLoading(false);
                });
            })
            .catch(error => {
                renderError(error);
                setLoading(false);
            });
    }, [id]);

    // Cadastros que os filtros de referência usam (categoria, centro de custo…).
    useEffect(() => {
        const needed = new Set(
            (dataset?.fields ?? []).filter(field => field.reference).map(field => field.reference)
        );

        needed.forEach(reference => {
            if (references[reference] || !REFERENCE_ENDPOINTS[reference]) return;

            api.get(REFERENCE_ENDPOINTS[reference])
                .then(res => setReferences(current => ({ ...current, [reference]: res.data.resources })))
                .catch(() => {});
        });
    }, [dataset]);

    // Prévia com atraso: digitar num filtro de texto disparava uma consulta
    // por tecla — e cada uma varre o período inteiro no banco do tenant.
    useEffect(() => {
        if (loading || !dataset) return;

        if (chosenColumns.length === 0 || !hasPeriod) {
            setResult(null);
            return;
        }

        const timer = setTimeout(run, PREVIEW_DELAY);

        return () => clearTimeout(timer);
    }, [dataset, config.columns, config.group_by, config.filters, config.date_field, config.start_date, config.end_date, config.order_by]);

    const patch = changes => setConfig(current => ({ ...current, ...changes }));

    const run = () => {
        setRunning(true);

        api.post(`/report/run`, { dataset: datasetKey, config: { ...config, columns: chosenColumns } })
            .then(res => setResult(res.data.resource))
            .catch(renderError)
            .then(() => setRunning(false));
    };

    const changeDataset = key => {
        // Trocar de fonte invalida coluna, filtro e ordenação: são campos de
        // outra fonte. Zerar é mais honesto que manter o que não existe mais.
        setDatasetKey(key);
        setConfig(defaultConfig(datasets.find(item => item.key === key)));
        setResult(null);
    };

    const changeGroupBy = key => {
        const group_by = key ? [key] : [];
        const measureKeys = measures.map(field => field.key);
        const stillAvailable = column => key ? column === key || measureKeys.includes(column) : true;
        const columns = key && !config.columns.includes(key) ? [...config.columns, key] : config.columns;

        patch({
            group_by,
            columns,
            order_by: config.order_by && stillAvailable(config.order_by.field) ? config.order_by : null,
        });
    };

    const toggleColumn = field => {
        const active = config.columns.includes(field.key);
        const columns = active
            ? config.columns.filter(key => key !== field.key)
            : [...config.columns, field.key];

        patch({
            columns,
            order_by: active && config.order_by?.field === field.key ? null : config.order_by,
        });
    };

    const addFilter = () => {
        const field = dataset?.fields?.find(item => item.filterable);
        if (!field) return;

        const operator = filterOperatorFor(field);

        patch({ filters: [...config.filters, { field: field.key, operator, value: emptyFilterValue(operator) }] });
    };

    const changeFilterField = (index, key) => {
        const field = dataset.fields.find(item => item.key === key);
        const operator = filterOperatorFor(field);

        updateFilter(index, { field: key, operator, value: emptyFilterValue(operator) });
    };

    const updateFilter = (index, changes) => {
        patch({ filters: config.filters.map((filter, position) => position === index ? { ...filter, ...changes } : filter) });
    };

    const removeFilter = index => {
        patch({ filters: config.filters.filter((filter, position) => position !== index) });
    };

    const filterValueOptions = field => {
        if (field?.options) return field.options;

        if (field?.reference === 'finance_category') {
            // A sintética entra na lista de propósito: filtrar por ela traz as
            // filhas (o backend expande a subárvore), que é o que se espera de
            // "despesas administrativas".
            return flattenCategories(references.finance_category ?? []).map(category => ({
                label: `${' '.repeat(category.depth * 3)}${getCategoryLabel(category)}`,
                value: category.id,
            }));
        }

        if (field?.reference) {
            return (references[field.reference] ?? []).map(item => ({
                label: item.name ?? item.trade_name ?? item.company_name,
                value: item.id,
            }));
        }

        return null;
    };

    const renderFilterValue = (filter, index) => {
        const field = dataset.fields.find(item => item.key === filter.field);

        if (filter.operator === 'between') {
            const value = filter.value ?? {};

            return (
                <div className="filter-range">
                    <InputDate label={`De`} format={`YYYY-MM-DD`} value={value.from ?? ''} change={from => updateFilter(index, { value: { ...value, from: from || null } })} />
                    <InputDate label={`Até`} format={`YYYY-MM-DD`} value={value.to ?? ''} change={to => updateFilter(index, { value: { ...value, to: to || null } })} />
                </div>
            );
        }

        if (filter.operator === 'in') {
            return (
                <InputSelect
                    isMulti
                    menuInBody
                    options={filterValueOptions(field) ?? []}
                    value={Array.isArray(filter.value) ? filter.value : []}
                    change={value => updateFilter(index, { value })}
                    noOptionsText={`Nenhuma opção`}
                />
            );
        }

        return <Input placeholder={`contém…`} floating={false} value={filter.value ?? ''} change={value => updateFilter(index, { value })} />;
    };

    const sortableOptions = [...dimensions, ...measures]
        .filter(field => chosenColumns.includes(field.key) && field.sortable !== false)
        .map(field => ({ label: field.label, value: field.key }));

    // O que o backend faz sem ordenação escolhida, dito com todas as letras:
    // "Padrão" sozinho não conta por onde a tabela vai começar.
    const defaultMeasure = measures.find(field => chosenColumns.includes(field.key));
    const defaultColumn = [...dimensions, ...measures].find(field => chosenColumns.includes(field.key));
    const defaultOrderLabel = defaultMeasure
        ? `Padrão (${defaultMeasure.label}, maior primeiro)`
        : defaultColumn ? `Padrão (${defaultColumn.label})` : 'Padrão';

    const canSave = name.trim() !== '' && !!datasetKey && chosenColumns.length > 0 && hasPeriod;

    const save = () => {
        setSaving(true);

        const payload = { name, description: description || null, dataset: datasetKey, config: { ...config, columns: chosenColumns } };
        const request = isNew ? api.post(`/report`, payload) : api.put(`/report/${id}`, payload);

        request
            .then(res => {
                toast.success('Relatório salvo');
                navigate(`/report/${res.data.resource.id}`);
            })
            .catch(renderError)
            .then(() => setSaving(false));
    };

    return (
        <PanelTemplate id="report-edit">
            <PageHeader
                left={
                    <div>
                        <button type="button" className="report-back" onClick={() => navigate('/report')}>Relatórios</button>
                        <h1>{isNew ? 'Novo relatório' : 'Editar relatório'}</h1>
                    </div>
                }
                right={
                    <>
                        <Button type={`secondary`} size={`small`} text={`Cancelar`} action={() => navigate('/report')} />
                        <Button type={`primary`} size={`small`} text={`Salvar relatório`} action={save} loading={saving} disabled={!canSave} />
                    </>
                }
            />

            <div className="report-builder">
                <div className="builder-config">
                    <Box title={`Configuração`}>
                        <Input label={`Nome do relatório`} value={name} change={setName} />
                        <Input label={`Descrição`} value={description} change={setDescription} />

                        {datasets.length > 1 &&
                            <InputSelect
                                label={`Fonte de dados`}
                                options={datasets.map(item => ({ label: item.label, value: item.key }))}
                                value={datasetKey}
                                change={changeDataset}
                            />
                        }
                        {dataset && <span className="builder-hint">{datasets.length > 1 ? dataset.description : `${dataset.label}: ${dataset.description}`}</span>}

                        {dataset &&
                            <>
                                <div className="builder-section">
                                    <label>Período <span className="required-tag">Obrigatório</span></label>
                                    <InputSelect
                                        label={`Por data de`}
                                        options={Object.entries(dataset.date_fields).map(([value, label]) => ({ label, value }))}
                                        value={config.date_field}
                                        change={value => patch({ date_field: value })}
                                    />
                                    <div className="row">
                                        <div className="col-6">
                                            <InputDate label={`De`} format={`YYYY-MM-DD`} value={config.start_date} change={value => patch({ start_date: value ?? '' })} />
                                        </div>
                                        <div className="col-6">
                                            <InputDate label={`Até`} format={`YYYY-MM-DD`} value={config.end_date} change={value => patch({ end_date: value ?? '' })} />
                                        </div>
                                    </div>
                                </div>

                                <div className="builder-section">
                                    <label>Agrupamento</label>
                                    <InputSelect
                                        menuInBody
                                        options={[{ label: 'Não agrupar (título a título)', value: '' }, ...allDimensions.map(field => ({ label: field.label, value: field.key }))]}
                                        value={config.group_by[0] ?? ''}
                                        change={changeGroupBy}
                                    />
                                    <span className="builder-hint">
                                        {grouped
                                            ? `Cada linha soma os títulos de um mesmo ${dimensions[0]?.label.toLowerCase() ?? 'grupo'}.`
                                            : 'Sem agrupamento, cada linha é um título. Agrupe para somar os valores por cliente, categoria, mês…'}
                                    </span>
                                </div>

                                <div className="builder-section">
                                    <label>Colunas <span className="builder-count">{chosenColumns.length} {chosenColumns.length === 1 ? 'escolhida' : 'escolhidas'}</span></label>

                                    {!grouped &&
                                        <>
                                            <span className="builder-group-label">Informações do título</span>
                                            {dimensions.map(field => (
                                                <div key={field.key} className="builder-field">
                                                    <button type="button" className={`field-check${config.columns.includes(field.key) ? ' active' : ''}`} onClick={() => toggleColumn(field)}>
                                                        <span className="check"><Icon name="check" size={11} strokeWidth={3.4} /></span>
                                                        {field.label}
                                                    </button>
                                                </div>
                                            ))}
                                        </>
                                    }

                                    <span className="builder-group-label">Valores</span>
                                    {measures.map(field => (
                                        <div key={field.key} className="builder-field">
                                            <button type="button" className={`field-check${config.columns.includes(field.key) ? ' active' : ''}`} onClick={() => toggleColumn(field)}>
                                                <span className="check"><Icon name="check" size={11} strokeWidth={3.4} /></span>
                                                {field.label}
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <div className="builder-section">
                                    <label>Filtros</label>
                                    {config.filters.map((filter, index) => (
                                        <div key={index} className="builder-filter">
                                            <div className="filter-head">
                                                <InputSelect
                                                    menuInBody
                                                    options={dataset.fields.filter(field => field.filterable).map(field => ({ label: field.label, value: field.key }))}
                                                    value={filter.field}
                                                    change={value => changeFilterField(index, value)}
                                                />
                                                <button type="button" className="filter-remove" onClick={() => removeFilter(index)} title="Remover filtro">
                                                    <Icon name="close" size={14} />
                                                </button>
                                            </div>
                                            {renderFilterValue(filter, index)}
                                        </div>
                                    ))}
                                    <Button type={`link`} size={`small`} text={`Adicionar filtro`} action={addFilter} />
                                </div>

                                <div className="builder-section">
                                    <label>Ordenação</label>
                                    <div className="builder-order">
                                        <InputSelect
                                            menuInBody
                                            options={[{ label: defaultOrderLabel, value: '' }, ...sortableOptions]}
                                            value={config.order_by?.field ?? ''}
                                            change={value => patch({ order_by: value ? { field: value, direction: config.order_by?.direction ?? 'asc' } : null })}
                                        />
                                        <InputSelect
                                            menuInBody
                                            options={ORDER_DIRECTIONS}
                                            value={config.order_by?.direction ?? 'asc'}
                                            isDisabled={!config.order_by?.field}
                                            change={value => patch({ order_by: { ...config.order_by, direction: value } })}
                                        />
                                    </div>
                                </div>

                                {grouped &&
                                    <div className="builder-section">
                                        <label>Gráfico</label>
                                        <InputSelect
                                            options={CHART_TYPES}
                                            value={config.chart?.type ?? 'bar'}
                                            change={value => patch({ chart: { type: value } })}
                                        />
                                    </div>
                                }
                            </>
                        }
                    </Box>
                </div>

                <div className="builder-preview">
                    <Box
                        title={result ? `Prévia · ${result.rows.length} ${result.rows.length === 1 ? 'linha' : 'linhas'}` : 'Prévia'}
                        className={`no-padding`}
                    >
                        <ReportResult
                            result={hasPeriod ? result : null}
                            chartType={grouped ? (config.chart?.type ?? 'bar') : 'none'}
                            loading={running || loading}
                            emptyMessage={hasPeriod ? `Escolha as colunas à esquerda para ver a prévia.` : `Informe o período para ver a prévia.`}
                        />
                    </Box>
                </div>
            </div>
        </PanelTemplate>
    );
};

export default ReportEdit;
